import { Dashboard } from "@/components/dashboard/Dashboard/Dashboard";
import { VStack } from "@/components/layout/VStack";
import { useView } from "@/context/viewContext";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAttendance } from "../hooks/useAttendance";
import { HStack } from "@/components/layout/HStack";
import { Skeleton } from "@/components/ui/Skeleton/Skeleton";
import { format } from "date-fns";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/Empty/Empty";
import { Button } from "@/components/ui/Button/Button";
import { BookOpen, Check, ListChecks, Save, UsersRound, X } from "lucide-react";
import { AttendanceTable } from "../components/AttendanceDataTable";
import { buildAttendanceEntries } from "../utils/buildAttendanceEntries";

const AttendancePage = () => {
  const { classId } = useParams();
  const { viewAs } = useView();

  const [attendance, setAttendance] = useState<{
    class: any;
    session: any;
    exploration: any;
    roster: {
      student: {
        _id: string;
        name: string;
        email: string;
        gradYear: number;
      };
      present: boolean;
      reason: string;
    }[];
  }>({
    roster: [],
    class: undefined,
    session: undefined,
    exploration: undefined,
  });
  const [entries, setEntries] = useState<
    {
      studentId: string;
      present: boolean;
    }[]
  >([]);

  const { loadAttendance, saveAttendance, loading } = useAttendance();

  useEffect(() => {
    if (!classId) return;
    loadAttendance(classId, setAttendance);
  }, [classId]);

  useEffect(() => {
    setEntries(buildAttendanceEntries(attendance.roster));
  }, [attendance.roster]);

  useEffect(() => {
  }, [entries]);

  const setAllPresent = () => {
    setEntries((prev) =>
      prev.map((e) => ({
        ...e,
        present: true,
      })),
    );
  };

  const setAllAbsent = () => {
    setEntries((prev) =>
      prev.map((e) => ({
        ...e,
        present: false,
      })),
    );
  };

  const onSave = () => {
    if (!classId) return;
    saveAttendance(classId, entries);
  };

  return (
    <Dashboard
      activePage={
        ["sys-admin", "administrator"].includes(viewAs)
          ? "/explorations"
          : "/explorations/me"
      }
    >
      {loading.load ? (
        <VStack>
          <HStack align="center">
            <h2
              style={{
                fontSize: "var(--text-2xl)",
                lineHeight: "var(--line-height-2xl)",
                fontWeight: "var(--font-bold)",
              }}
            >
              Attendance —
            </h2>
            <Skeleton style={{ width: "240px", height: "32px" }} />
          </HStack>
          <HStack align="center">
            <p style={{ color: "var(--muted-foreground)" }}>
              Take attendance for{" "}
            </p>
            <Skeleton style={{ width: "240px", height: "14px" }} />
          </HStack>
        </VStack>
      ) : !attendance.class ? (
        <VStack>
          <h2
            style={{
              fontSize: "var(--text-2xl)",
              lineHeight: "var(--line-height-2xl)",
              fontWeight: "var(--font-bold)",
            }}
          >
            Attendance
          </h2>
          <p style={{ color: "var(--muted-foreground)" }}>
            Take attendance for an exploration.
          </p>
        </VStack>
      ) : (
        <HStack justify="space-between" gap="1rem" wrap>
          <VStack>
            <h2
              style={{
                fontSize: "var(--text-2xl)",
                lineHeight: "var(--line-height-2xl)",
                fontWeight: "var(--font-bold)",
              }}
            >
              Attendance — {attendance.exploration.name} -{" "}
              {format(attendance.class.date, "MMMM dd, yyyy")}
            </h2>
            <p style={{ color: "var(--muted-foreground)" }}>
              Take attendance for {attendance.exploration.name} —{" "}
              {attendance.session.name} -{" "}
              {format(attendance.class.date, "MMMM dd, yyyy")}
            </p>
          </VStack>
          <HStack style={{ flexWrap: "wrap" }}>
            <Button variant="outline" onClick={setAllPresent}>
              <Check /> All Present
            </Button>
            <Button variant="outline" onClick={setAllAbsent}>
              <X /> Clear All
            </Button>
            <Button onClick={onSave}>
              <Save /> Save Attendance
            </Button>
          </HStack>
        </HStack>
      )}
      {loading.load ? (
        <Skeleton style={{ width: "100%", height: "512px" }} />
      ) : !attendance.class ? (
        <Empty style={{ borderColor: "var(--secondary-border)" }}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ListChecks />
            </EmptyMedia>
            <EmptyTitle>Attendance Not Found</EmptyTitle>
            <EmptyDescription>
              Attendance not found, please refresh the page, or select a new
              attendance date.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button asChild style={{ textDecoration: "none" }}>
              <Link to="/explorations/me">
                <BookOpen /> Go To My Exploraations
              </Link>
            </Button>
          </EmptyContent>
        </Empty>
      ) : attendance.roster.length < 1 ? (
        <Empty style={{ borderColor: "var(--secondary-border)" }}>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <UsersRound />
            </EmptyMedia>
            <EmptyTitle>No Students Found</EmptyTitle>
            <EmptyDescription>
              No students were found enrolled in this exploration, if you
              believe this is an error please refresh the page.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <AttendanceTable
          data={attendance.roster}
          entries={entries}
          setEntries={setEntries}
        />
      )}
    </Dashboard>
  );
};

export default AttendancePage;
